import { Input } from "@/components/ui/input";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { CheckCircle2 } from "lucide-react";
import OnboardingGradeHintFlow from "../reader/ onboarding/flows/OnboardingGradeHintFlow";
import { useFlow } from "@frigade/react";
import { config } from "@/config";

interface GradeEditorProps {
  className?: string;
  feedbackUnlocked: boolean;
}

export const GradeEditor = ({ className, feedbackUnlocked }: GradeEditorProps) => {
  const grading = useAppState((state) => state.grading);
  const [finalGrade, setFinalGrade] = useState(grading?.grade || "");
  const [isSaved, setIsSaved] = useState(true);
  const { documentId } = useParams();
  const { updateFinalGrade } = useApi();
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { flow } = useFlow(config.frigade.flows.gradeHint);

  const isPublished = grading?.feedback?.status === "published";
  const isEditable = feedbackUnlocked || !isPublished;

  // Update local state when grading changes
  useEffect(() => {
    setFinalGrade(grading?.grade || "");
  }, [grading?.grade]);

  // Debounced API update function
  const debouncedUpdateGrade = useCallback(
    (grade: string) => {
      if (!documentId) return;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        await updateFinalGrade(documentId, grade);
        setIsSaved(true);
        if (grade && flow && !flow.isCompleted) {
          flow.complete();
        }
      }, 1000);
    },
    [documentId, flow, updateFinalGrade]
  );

  // Handle input change
  const handleFinalGradeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFinalGrade(value);
    setIsSaved(false);
    debouncedUpdateGrade(value);
  };

  // Update saved state when grading changes from external source
  useEffect(() => {
    if (grading?.grade === finalGrade) {
      setIsSaved(true);
    }
  }, [grading?.grade, finalGrade]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`py-6 ${className || ''}`}>
      <OnboardingGradeHintFlow />
      
      <div className="flex justify-between px-20 items-center">
        <label className="font-medium text-base">Final Grade</label>
        <div className="flex items-center gap-2">
          <label
            htmlFor="final-grade"
            className="font-medium text-base mr-6"
          >
            Grade
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="final-grade"
              type="text"
              value={finalGrade}
              onChange={handleFinalGradeChange}
              disabled={!isEditable}
              placeholder="None"
              className="w-[120px]"
            />
            {finalGrade && (
              <CheckCircle2 
                className={`h-5 w-5 transition-colors ${
                  isSaved ? "text-green-500" : "text-gray-300"
                }`} 
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
